import {
  Badge,
  Button,
  Group,
  Input,
  Modal,
  Select,
  SelectProps,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { IconPlus, IconSlash } from "@tabler/icons-react"
import { zodResolver } from "mantine-form-zod-resolver"
import { useRef } from "react"
import { z } from "zod"

import { CHART_TYPES } from "@costory/types/endpoints/dashboard"
import { SavedViewsResponses } from "@costory/types/endpoints/savedViews"
import { ComponentType } from "@costory/types/prisma-client"
import { Pick } from "@costory/types/prisma-client/runtime/library"

import { Chart } from "@costory/front/components/charts/Chart"
import { ChartTypeSelector } from "@costory/front/components/dashboard/ChartTypeSelector"
import { NewSavedView } from "@costory/front/components/dashboard/NewSavedView"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { useListSavedViewsQuery } from "@costory/front/queries/savedViews"

const addWidgetSchema = z.object({
  title: z.string(),
  viewId: z.string(),
  chartType: z.enum(CHART_TYPES),
})

export type AddWidgetForm = z.infer<typeof addWidgetSchema>

type Props = {
  onAdd: (widgetConfig: AddWidgetForm) => void
  savedViews: (SavedViewsResponses.SavedView & {
    tags: { id: string; name: string; color: string }[]
  })[]
  componentType?: ComponentType
}

const _AddWidget = ({ savedViews, onAdd }: Props) => {
  const [
    isNewSavedViewOpen,
    { open: openNewSavedView, close: closeNewSavedView },
  ] = useDisclosure(false)

  const form = useForm<AddWidgetForm>({
    validate: zodResolver(addWidgetSchema),
  })

  const resetFiltersRef = useRef<(() => void) | null>(null)

  const handleCloseModal = () => {
    resetFiltersRef.current?.()
    closeNewSavedView()
  }

  const handleSaveView = async (savedViewId: string, savedViewName: string) => {
    form.setValues({
      viewId: savedViewId,
      title: savedViewName,
    })

    closeNewSavedView()
  }

  const renderSelectOption: SelectProps["renderOption"] = ({ option }) => {
    const view = savedViews.find((v) => v.id === option.value)
    return (
      <Group justify="space-between" w="100%" wrap="nowrap">
        <Tooltip maw="60%" multiline label={option.label}>
          <Text lineClamp={1}>{option.label}</Text>
        </Tooltip>
        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
          {view?.datePreset && (
            <Badge size="sm" color="blue">
              {view.datePreset}
            </Badge>
          )}
        </Group>
      </Group>
    )
  }

  return (
    <Stack gap={32}>
      <Stack gap={8}>
        <Text fw="bold">
          1. Choose the view to display or create a new one{" "}
        </Text>
        <Group gap="md">
          <Select
            flex={1}
            searchable
            {...form.getInputProps("viewId")}
            placeholder="Select a saved view"
            data={savedViews.map((view) => ({
              label: view.name,
              value: view.id,
            }))}
            renderOption={renderSelectOption}
            onChange={(value, option) => {
              if (!value) return
              form.setValues({ viewId: value, title: option.label })
            }}
          />
          <IconSlash />
          <Button onClick={openNewSavedView} leftSection={<IconPlus />}>
            Create new view
          </Button>
        </Group>
      </Stack>
      <Stack gap={8}>
        <Text fw="bold">2. Choose the visualization</Text>
        <ChartTypeSelector {...form.getInputProps("chartType")} />
      </Stack>
      {form.values.viewId && form.values.chartType && (
        <Stack
          style={{
            borderTop: "1px solid var(--mantine-color-borderSubtle-0)",
          }}
          pt={24}
        >
          <Text fw="bold" fz="lg">
            Chart Preview
          </Text>
          <Group w="100%" wrap="nowrap">
            <Stack w="60%" align="center" style={{ zIndex: 1000 }}>
              <Chart
                chartType={form.values.chartType}
                height={250}
                filters={
                  savedViews.find((view) => view.id === form.values.viewId)!
                }
                withLegend={false}
              />
            </Stack>
            <Stack
              w="40%"
              h="100%"
              align="center"
              justify="center"
              gap={24}
              p={24}
              style={{
                borderLeft: "1px solid var(--mantine-color-borderSubtle-0)",
              }}
            >
              <Stack gap={8} w="100%">
                <Text fw="bold">3. Name your widget</Text>
                <Input {...form.getInputProps("title")} />
              </Stack>
              <Text ta="center">
                You can change the parameters before adding the widget to your
                dashboard
              </Text>
              <Button size="lg" onClick={() => onAdd(form.values)}>
                Add to Dashboard
              </Button>
            </Stack>
          </Group>
        </Stack>
      )}

      <Modal
        opened={isNewSavedViewOpen}
        onClose={handleCloseModal}
        size="100%"
        fullScreen
        title={<Title order={5}>Create a new saved view</Title>}
        styles={{
          header: {
            height: 60,
          },
        }}
      >
        <NewSavedView
          onResetFilters={(resetFn) => {
            resetFiltersRef.current = resetFn
          }}
          onSaveView={handleSaveView}
        />
      </Modal>
    </Stack>
  )
}

export const AddWidget = ({
  onAdd,
  componentType,
}: Pick<Props, "onAdd" | "componentType">) => {
  const savedViewsQuery = useListSavedViewsQuery()

  return (
    <QueryWrapper
      query={savedViewsQuery}
      emptyAlert={{
        title: "No saved views",
        message:
          "You don't have any view saved for now, go to the Explorer and create your first view!",
      }}
    >
      {({ data }) => (
        <_AddWidget
          onAdd={onAdd}
          savedViews={data.filter(({ type }) =>
            componentType === ComponentType.COMMUNITY
              ? type === componentType
              : true,
          )}
        />
      )}
    </QueryWrapper>
  )
}
